import clsx from 'clsx';

import { Skeleton } from '../Skeleton';

const CHART_SKELETON_GRID_LINES = ['l1', 'l2', 'l3', 'l4', 'l5'];

// Filled-area silhouette in a 1000x100 viewBox (y inverted, baseline at 100), closed back to
// the baseline so it fills like the real area series.
const CHART_SKELETON_AREA =
    'M0,72 C 80,55 160,48 240,52 C 330,56 380,62 450,56 C 520,50 560,38 620,42 C 680,46 700,80 760,74 C 820,68 840,46 900,50 C 950,53 980,42 1000,40 L1000,100 L0,100 Z';

// The area shape is the alpha mask over a `.shimmer` background, so only the area shimmers.
const CHART_SKELETON_MASK = `url("data:image/svg+xml,${encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1000 100' preserveAspectRatio='none'><path d='${CHART_SKELETON_AREA}' fill='black'/></svg>`
)}")`;

// The value axis is sized to its widest tick, so this can only approximate it: the chart's 20px
// left inset, a label in the hundreds-to-thousands range, and the 8px tick gap.
const CHART_SKELETON_Y_AXIS_GUTTER = 56;

/**
 * Compact skeleton for an inline chart (e.g. inside a table cell).
 * Renders a row of small bars with pseudo-random but relatively flat heights.
 */
export function InlineChartSkeleton(props: { height?: number; className?: string }) {
    const { height = 32, className } = props;
    const bars = [
        'h-[40%]',
        'h-[55%]',
        'h-[35%]',
        'h-[50%]',
        'h-[45%]',
        'h-[30%]',
        'h-[55%]',
        'h-[40%]',
        'h-[50%]',
        'h-[35%]',
        'h-[45%]',
        'h-[55%]',
        'h-[40%]',
    ];
    return (
        <div className={clsx('flex items-end gap-[2px]', className)} style={{ height }}>
            {bars.map((h, i) => (
                <Skeleton key={`bar-${i}-${h}`} className={clsx('flex-1 rounded-t-sm', h)} />
            ))}
        </div>
    );
}

export interface ChartSkeletonProps {
    /**
     * Shape to stand in for. Match it to the chart behind the fallback, or the plot changes
     * shape as the real chart loads.
     * @default 'line'
     */
    kind?: 'line' | 'bar';

    /**
     * The height of the chart, matching the `height` given to the chart itself.
     */
    height?: number;

    /**
     * The class name to apply to the chart container, matching the chart's own `className`.
     */
    className?: string;
}

/**
 * Skeleton shaped like the chart it stands in for: an area silhouette for {@link LineChart},
 * a row of bars for {@link BarChart}. Small enough to sit in a table cell? Use
 * {@link InlineChartSkeleton}.
 */
export function ChartSkeleton(props: ChartSkeletonProps) {
    const { kind = 'line', height = 320, className } = props;

    if (kind === 'bar') {
        return (
            <div className={clsx('flex flex-col', className)}>
                <div className="mx-6 mt-6 flex flex-row items-end gap-3" style={{ height }}>
                    <Skeleton className="size-8 h-full" />
                    <Skeleton className="size-8 h-[80%]" />
                    <Skeleton className="size-8 h-[60%]" />
                    <Skeleton className="size-8 h-[80%]" />
                    <Skeleton className="size-8 h-[40%]" />
                    <Skeleton className="size-8 h-[20%]" />
                    <Skeleton className="size-8 h-[60%]" />
                    <Skeleton className="size-8 h-[80%]" />
                    <Skeleton className="size-8 h-[60%]" />
                    <Skeleton className="size-8 h-[40%]" />
                    <Skeleton className="size-8 h-[20%]" />
                    <Skeleton className="size-8 h-full" />
                    <Skeleton className="size-8 h-[80%]" />
                    <Skeleton className="size-8 h-[60%]" />
                    <Skeleton className="size-8 h-[80%]" />
                    <Skeleton className="size-8 h-[40%]" />
                    <Skeleton className="size-8 h-[20%]" />
                    <Skeleton className="size-8 h-[60%]" />
                    <Skeleton className="size-8 h-[80%]" />
                    <Skeleton className="size-8 h-[60%]" />
                    <Skeleton className="size-8 h-[40%]" />
                    <Skeleton className="size-8 h-[20%]" />
                </div>
                <div className="flex h-6 flex-row items-end justify-center">
                    <Skeleton className="h-4 w-20" />
                </div>
            </div>
        );
    }

    // Mirrors the real LineChart layout (`mt-6 mr-6 mb-2`, y-axis gutter, x-axis row, legend)
    // so the container doesn't jump when the chart loads.
    return (
        <div className={className}>
            <div
                className="mt-6 mr-6 mb-2 flex flex-col"
                style={{ height }}
                aria-busy="true"
                aria-hidden="true"
            >
                <div className="flex min-h-0 flex-1">
                    <div
                        className="flex flex-col justify-between pr-2"
                        style={{ width: CHART_SKELETON_Y_AXIS_GUTTER }}
                    />
                    <div className="relative flex-1">
                        <div className="absolute inset-0 flex flex-col justify-between">
                            {CHART_SKELETON_GRID_LINES.map((id) => (
                                <div key={id} className="border-tint border-t border-dashed" />
                            ))}
                        </div>
                        <div
                            className="absolute inset-0 shimmer"
                            style={{
                                maskImage: CHART_SKELETON_MASK,
                                WebkitMaskImage: CHART_SKELETON_MASK,
                                maskRepeat: 'no-repeat',
                                WebkitMaskRepeat: 'no-repeat',
                                maskSize: '100% 100%',
                                WebkitMaskSize: '100% 100%',
                            }}
                        />
                    </div>
                </div>
                <div
                    className="h-2.5 w-full pt-3"
                    style={{ paddingLeft: CHART_SKELETON_Y_AXIS_GUTTER }}
                />
                <div className="flex justify-center gap-4 pt-3">
                    <Skeleton className="h-2.5 w-14" />
                    <Skeleton className="h-2.5 w-14" />
                </div>
            </div>
        </div>
    );
}
